import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, CheckCircle2, Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { typography } from '@/lib/design-tokens/typography';
import { spacing } from '@/lib/design-tokens/spacing';
import { gradients, shadows } from '@/lib/design-tokens/colors';
import { cn } from '@/lib/utils';

interface UpgradePromptProps {
  feature: string;
  description: string;
  tier: 'starter' | 'pro';
  icon?: React.ReactNode;
  benefits?: string[];
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({ 
  feature, 
  description,
  tier, 
  icon,
  benefits 
}) => {
  const tierName = tier === 'starter' ? 'Starter' : 'Pro';
  const tierPrice = tier === 'starter' ? '€10' : '€30';

  return (
    <Card className={cn("max-w-2xl mx-auto overflow-hidden", shadows.lifted)}>
      {/* Gradient header section */}
      <div className={cn(
        gradients.upgradeCard,
        gradients.upgradeCardDark,
        "relative overflow-hidden"
      )}>
        <CardContent className={cn(spacing.featureCard, "text-center relative")}>
          {/* Decorative elements */}
          <div className="absolute top-4 right-4 opacity-20">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <div className="absolute bottom-4 left-4 opacity-20">
            <Sparkles className="h-6 w-6 text-accent" />
          </div>
          
          {/* Icon with glow effect */}
          <div className={cn(
            gradients.iconGlow,
            gradients.iconGlowHover,
            "inline-flex p-6 rounded-full transition-all duration-300",
            spacing.featureIconMargin
          )}>
            {icon || <Lock className="h-16 w-16 text-primary" />}
          </div>
          
          {/* Feature heading */}
          <h3 className={cn(typography.featureTitle, "mb-3")}>
            {feature}
          </h3>
          
          {/* Description */}
          <p className={cn(typography.featureDescription, "max-w-xl mx-auto")}>
            {description}
          </p>
        </CardContent>
      </div>

      {/* Benefits section */}
      <CardContent className={cn(spacing.cardPadding, "text-center")}>
        {benefits && benefits.length > 0 && (
          <div className={cn(spacing.featureContentSpacing, "text-left max-w-md mx-auto mb-8")}>
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 group animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className={typography.benefitText}>{benefit}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* Pricing badge */}
        <div className={cn(
          "inline-flex items-baseline gap-1 px-6 py-3 rounded-xl border-2 mb-8",
          "bg-accent/10 border-accent/20",
          "hover:bg-accent/20 transition-colors"
        )}>
          <span className={typography.priceAmount}>{tierPrice}</span>
          <span className={typography.priceSuffix}>/month</span>
        </div>
        
        {/* CTA button */}
        <Link to="/subscription" className="block">
          <Button 
            size="lg" 
            className="h-12 px-8 text-base font-semibold hover:scale-105 transition-transform"
          >
            Upgrade to {tierName}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};
